import { API } from "@swivl-mono/api-helper";
import { AppInterface, BOT_VOICES, NumberPlatform, defaultBotVoice, findBotVoice } from "@swivl-mono/types";
import { Button, ButtonColor, ButtonSize, Input, Select, Spinner } from "@swivl-mono/ui";
import React from "react";

import { ConversationRestrictionsControls } from "../../../../Components/ConversationRestrictionsControls/ConversationRestrictionsControls";
import { QuestsDropdown } from "../../../../Components/Dropdowns/QuestsDropdown/QuestsDropdown";
import AppsModel from "../../../../Models/Apps/Apps.Model";
import { Member } from "../../../../Models/Member/Member.Current";
import { NavBar } from "../../../../Models/NavBar/NavBar.Model";
import { QuestsModel } from "../../../../Models/Quests/Quests.Model";
import { Field } from "../AppInstanceField";

interface Props {
  app: AppInterface;
  selected?: boolean;
  /** When false, fields are read-only and edits are staged until Save. */
  disabled?: boolean;
}

interface State {
  /** Move in flight, and the last outcome to show. */
  routingBusy: boolean;
  routingMessage: string;
  /** Which system answers this number; undefined until known, or if unknown. */
  platform?: NumberPlatform;
  /** New system on a subaccount with no conference app yet: calls won't reach the bot until setup is finished. */
  conferenceAppMissing: boolean;
  platformChecked: boolean;
}

export default class TwilioView extends React.Component<Props, State> {
  unsubscribeQuests?: () => void;
  state: State = { routingBusy: false, routingMessage: "", platformChecked: false, conferenceAppMissing: false };

  constructor(props) {
    super(props);

    this.onChange = this.onChange.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.handleSwitchSystem = this.handleSwitchSystem.bind(this);
    this.handleVoiceChange = this.handleVoiceChange.bind(this);
  }

  /** The number's voice: a catalog pick, or the platform default when the default row is chosen. */
  handleVoiceChange(selected: string[]): void {
    const app = { ...this.props.app };
    const picked = findBotVoice(selected[0]);
    if (picked && !picked.isDefault) app.data.voiceSpeechModel = picked.id;
    else delete app.data.voiceSpeechModel;
    AppsModel.update(app, false);
  }

  async loadPlatform(): Promise<void> {
    const { app } = this.props;
    if (!Member.isSwivlStaff || !Member.orgId || !app?.id) return;
    this.setState({ platformChecked: false });
    try {
      const result = await API.TwilioSetup.getNumberPlatform(Member.orgId, app.id);
      this.setState({ platform: result.platform, conferenceAppMissing: result.conferenceAppMissing === true, platformChecked: true });
    } catch {
      this.setState({ platform: undefined, conferenceAppMissing: false, platformChecked: true });
    }
  }

  async handleSwitchSystem(platform: NumberPlatform): Promise<void> {
    const { app } = this.props;
    if (!Member.orgId || !app?.id) return;
    const target = platform === "snowy" ? "the new system" : "the old system";
    const finishingSetup = platform === "snowy" && this.state.platform === "snowy" && this.state.conferenceAppMissing;
    const prompt = finishingSetup
      ? `Finish the new-system setup for ${app.identifier}? Its subaccount gets the bot's conference app and the number stays on the new system.`
      : `Move ${app.identifier} to ${target}? Its calls and texts will go to ${target} right away.`;
    if (!window.confirm(prompt)) return;

    this.setState({ routingBusy: true, routingMessage: "" });
    try {
      const result = await API.TwilioSetup.switchNumberPlatform(Member.orgId, app.id, platform, false);
      if (!result.success) {
        this.setState({ routingMessage: result.error || `Couldn't move this number to ${target}.` });
        return;
      }
      let message = finishingSetup ? `${app.identifier} is set up on the new system.` : `${app.identifier} now goes to ${target}.`;
      const siblings = result.siblings ?? [];
      if (siblings.length > 0) {
        const many = siblings.length > 1;
        const list = siblings.map((s) => s.identifier).join(", ");
        const moveThemToo = window.confirm(
          `${siblings.length} other number${many ? "s" : ""} in this org ${many ? "are" : "is"} on the same Twilio account (${list}). Move ${many ? "them" : "it"} to ${target} too?`
        );
        if (moveThemToo) {
          const moved = await API.TwilioSetup.switchNumberPlatform(Member.orgId, app.id, platform, true);
          if (moved.success) {
            message = `${message} Moved ${siblings.length} other number${many ? "s" : ""} too.`;
          } else {
            // Every number was attempted; say exactly which moved and which did not.
            const outcomes = moved.results ?? [];
            const movedIds = outcomes.filter((r) => r.success && r.appId !== app.id).map((r) => r.identifier);
            const failedIds = outcomes.filter((r) => !r.success).map((r) => r.identifier);
            message = `${message}${movedIds.length ? ` Moved ${movedIds.join(", ")}.` : ""} Not moved: ${failedIds.join(", ") || "unknown"} — try again.`;
          }
        }
      }
      await AppsModel.load(true);
      await this.loadPlatform();
      this.setState({ routingMessage: message });
    } catch (e) {
      this.setState({ routingMessage: e instanceof Error ? e.message : `Couldn't move this number to ${target}.` });
    } finally {
      this.setState({ routingBusy: false });
    }
  }

  componentWillMount() {
    NavBar.set(<NavBar.Component.Back />, <NavBar.Component.Title> Twilio App Settings</NavBar.Component.Title>, null);
  }

  componentDidMount() {
    QuestsModel.loadQuestsList(false);
    // A class component, so re-render manually when quest names finish loading.
    this.unsubscribeQuests = QuestsModel.useState.subscribe(() => this.forceUpdate());
    if (this.props.selected) this.loadPlatform();
  }

  componentDidUpdate(prevProps: Props) {
    // Only the open card asks Twilio, so closed cards cost nothing.
    if (this.props.selected && (!prevProps.selected || prevProps.app?.id !== this.props.app?.id)) this.loadPlatform();
  }

  componentWillUnmount() {
    this.unsubscribeQuests?.();
  }
  onChange(e) {
    const app = { ...this.props.app };
    const name = e.target.name;
    const value = e.target.value;
    if (name === "phoneNumber") {
      app.identifier = value;
    } else {
      let token = app.token || {};
      token[name] = value;
      app.token = token;
    }

    AppsModel.update(app, false);
  }
  onBlur(e) {
    AppsModel.update(this.props.app, false);
  }

  render() {
    const { app, disabled } = this.props;
    var content;

    if (!app) {
      content = <Spinner />;
    } else {
      const token = app.token || {};
      const phoneNumber = app.identifier || "";
      const startOnQuest = app.data?.startOnQuest || "";

      content = (
        <div className="twilioValues flex flex-col gap-2">
          <Field label="Phone Number (ie. +17205551234)" disabled={disabled} value={phoneNumber}>
            <Input name="phoneNumber" value={phoneNumber} onChange={this.onChange} onBlur={this.onBlur} />
          </Field>
          <Field label="SID" disabled={disabled} value={token.accountSid}>
            <Input name="accountSid" value={token.accountSid} onChange={this.onChange} onBlur={this.onBlur} />
          </Field>
          <Field label="Auth Token" disabled={disabled} value={token.authToken ? "••••••••" : ""}>
            <Input type="password" name="authToken" value={token.authToken} onChange={this.onChange} onBlur={this.onBlur} />
          </Field>
          {/*
          <Input name="apiKey" value={token.apiKey} onChange={this.onChange} onBlur={this.onBlur} />
          <Input type={Input.Type.Password} name="apiSecret" value={token.apiSecret} onChange={this.onChange} onBlur={this.onBlur} /> 
          */}

          {/* <label>Instalation Instructions</label> */}
          {/* <div className="instructions">
                  To complete the instalation, you must set the SMS webhook for your  account to:<br/>
                  <SSCopyToClipboard
                    copyValue={"https://api.swivl.studio/apps/twilio"}
                    copiedComponent={
                      <div className="webhook">https://api.swivl.studio/apps/twilio <span>COPIED</span></div>
                    }
                    >
                    <div className="webhook">https://api.swivl.studio/apps/twilio <span>COPY</span></div>
                    </SSCopyToClipboard>

                  </div> */}

          {Member.isSwivlStaff && (
            <>
              <hr className="mt-5 mb-5" />
              <div className="flex flex-col gap-1">
                <label>Calls and texts routing</label>
                <p className="body-sm text-grey-900">
                  Choose which system answers calls and texts to this number. Move it to the new system when the location is ready to switch, or
                  switch it back to undo. If other numbers in this org share the same Twilio account, you&apos;ll be asked whether to move them too.
                </p>
                {!this.state.platformChecked && <p className="body-sm text-grey-600">Checking which system this number is on…</p>}
                {this.state.platformChecked && this.state.platform && (
                  <p className="body-sm text-grey-900">Currently on the {this.state.platform === "snowy" ? "new" : "old"} system.</p>
                )}
                {this.state.platformChecked && this.state.platform === "snowy" && this.state.conferenceAppMissing && (
                  <p className="body-sm text-orange-600">
                    This subaccount&apos;s new-system setup isn&apos;t finished: calls to this number won&apos;t reach the new system until it is.
                  </p>
                )}
                {this.state.platformChecked && !this.state.platform && (
                  <p className="body-sm text-grey-600">We couldn&apos;t tell which system this number is on.</p>
                )}
                {this.state.platformChecked && (
                  <div className="flex items-center gap-2">
                    {this.state.platform === "snowy" ? (
                      <>
                        {this.state.conferenceAppMissing && (
                          <Button
                            color={ButtonColor.ORANGE}
                            size={ButtonSize.SMALL}
                            disabled={this.state.routingBusy}
                            onClickAsync={() => this.handleSwitchSystem("snowy")}
                          >
                            {this.state.routingBusy ? "Working…" : "Finish new system setup"}
                          </Button>
                        )}
                        <Button
                          color={ButtonColor.WHITE}
                          size={ButtonSize.SMALL}
                          disabled={this.state.routingBusy}
                          onClickAsync={() => this.handleSwitchSystem("legacy")}
                        >
                          {this.state.routingBusy ? "Working…" : "Switch back to old system"}
                        </Button>
                      </>
                    ) : (
                      <Button
                        color={ButtonColor.WHITE}
                        size={ButtonSize.SMALL}
                        disabled={this.state.routingBusy}
                        onClickAsync={() => this.handleSwitchSystem("snowy")}
                      >
                        {this.state.routingBusy ? "Working…" : "Move to new system"}
                      </Button>
                    )}
                  </div>
                )}
                {this.state.routingMessage && <p className="body-sm text-grey-600">{this.state.routingMessage}</p>}
              </div>
            </>
          )}

          <hr className="mt-5 mb-5" />
          {this.state.platform === "snowy" ? (
            <Field
              label="Voice"
              disabled={disabled}
              value={findBotVoice(app.data?.voiceSpeechModel)?.displayName ?? `${defaultBotVoice().displayName} (default)`}
            >
              <Select
                name="voice"
                hideClear
                options={[
                  { value: "", label: `${defaultBotVoice().displayName} (default)` },
                  ...BOT_VOICES.filter((voice) => !voice.isDefault).map((voice) => ({ value: voice.id, label: voice.displayName }))
                ]}
                selectedValues={[findBotVoice(app.data?.voiceSpeechModel)?.id ?? ""]}
                onChange={this.handleVoiceChange}
              />
            </Field>
          ) : (
            <Field
              label={
                <>
                  Voice Speech Model &nbsp;
                  <a
                    href="https://www.twilio.com/docs/voice/conversationrelay/voice-configuration"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontWeight: 400, fontSize: "0.95em" }}
                  >
                    (Click to see available models. Copy VoiceID and paste below)
                  </a>
                </>
              }
              disabled={disabled}
              value={app.data?.voiceSpeechModel ?? ""}
            >
              <Input
                name="voiceSpeechModel"
                value={app.data?.voiceSpeechModel}
                onChange={(e) => {
                  const app = { ...this.props.app };
                  app.data.voiceSpeechModel = e.target.value;
                  AppsModel.update(app, false);
                }}
                onBlur={this.onBlur}
                placeholder="tnSpp4vdxKPjI9w0GnoV"
              />
            </Field>
          )}
          <Field label="Start on Quest" disabled={disabled} value={startOnQuest ? (QuestsModel.nameForQuest(startOnQuest) ?? startOnQuest) : ""}>
            <QuestsDropdown
              value={startOnQuest}
              name="startOnQuest"
              questSelected={(slug) => {
                const app = { ...this.props.app };
                app.data.startOnQuest = slug;
                AppsModel.update(app, false);
              }}
              onClear={() => {
                const app = { ...this.props.app };
                delete app.data.startOnQuest;
                AppsModel.update(app, false);
              }}
            />
          </Field>
          {/* <label>Initial Message</label>
          <Input name="initialMessage" value={app.data?.initialMessage} onChange={(e) => {
            const app = { ...this.props.app };
            app.data.initialMessage = e.target.value;
            AppsModel.update(app, true);
          }} onBlur={this.onBlur} placeholder="This call may be recorded and uses automated voice technology." /> */}
          <ConversationRestrictionsControls
            restrictionsData={app.data?.locationRestrictions || {}}
            updateRestrictionsData={(data) => {
              const app = { ...this.props.app };
              app.data.locationRestrictions = data;
              AppsModel.update(app, false);
            }}
            disabled={disabled}
          />
        </div>
      );
    }

    return <div className="TwilioView">{content}</div>;
  }
}
